import React, { useState, useEffect, useRef } from "react";
import { Search, Settings, Calendar, ChevronLeft, ChevronRight } from "lucide-react"; // Add Calendar, ChevronLeft, ChevronRight import
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchMemberApi,
  fetchLocationForTrackingApi,
} from "../../actioncreators/actioncreators";
import AddMember from "./AddMember";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import "./dashboard.css";
import Dialog from "@mui/material/Dialog";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  InfoWindow,
  Circle,
  Polyline,
} from "@react-google-maps/api";
import { useGoogleMaps } from "./useGoogleMaps";
import { toast } from "react-toastify";

const UserAvatar = ({ member }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (fullName) => {
    if (!fullName) return "";
    const names = fullName.trim().split(" ");
    if (names.length === 1) return names[0][0].toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  // If there's no image or if there was an error loading it, show initials
  if (!member.user_image || imageError) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
        {getInitials(member.full_name)}
      </div>
    );
  }

  // Try to render the image
  return (
    <img
      src={`data:image/jpeg;base64,${member.user_image}`}
      alt={member.full_name}
      className="w-8 h-8 rounded-full object-cover"
      onError={() => setImageError(true)}
    />
  );
};

const LocationTracking = () => {
  const { isLoaded: isMapsLoaded } = useGoogleMaps();

  const location = useLocation();
  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);

  // Super Admin / Admin org filtering support
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';
  const isAdmin = loginResponse?.role_name?.toUpperCase() === 'ADMIN' || loginResponse?.roleName?.toUpperCase() === 'ADMIN';
  // Org/sub-org context to pass for Super Admin (never org_id for Admin -
  // their own org is implicit via the JWT header) when drilled into a
  // specific org/sub-org via the header dropdown.
  const effectiveOrgId = isSuperAdmin && superAdminSelectedOrgId ? superAdminSelectedOrgId : null;

  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState({
    lat: 18.569970140993835,
    lng: 73.77190053167986,
  });
  const [zoomLevel, setZoomLevel] = useState(2); // State to manage zoom level
  const [selectedLocation, setSelectedLocation] = useState(null); // Location details
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const mapRef = useRef(null); // Reference to the map instance
  const [selectedMemberId, setSelectedMemberId] = useState(null); // State to track selected member
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Add this state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;

  const CustomDateInput = ({ value, onClick }) => (
    <div className="date-picker-wrapper" style={{ position: "relative", display: "inline-block" }}>
      <input
        type="text"
        value={value}
        onClick={onClick}
        readOnly
        className="text-sm font-bold p-2 bg-gray-200 rounded"
        style={{ paddingRight: "30px" }}
      />
      <Calendar
        onClick={onClick}
        className="calendar-icon"
        style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: "pointer",
          color: "#555",
          fontSize:"20px",
        }}
      />
    </div>
  );

  const handleAddMemberSuccess = () => {
    fetchMembers(); // Refresh member list after a new member is added
    setIsAddMemberOpen(false); // Close the dialog after success
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [currentPage, globalLocationFilter, superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

  // Auto-select user from navigation state (from Geofence Compliance Widget)
  useEffect(() => {
    if (location.state?.selectedUserId && members.length > 0) {
      const selectedUser = members.find(m => m.user_id === location.state.selectedUserId);
      if (selectedUser) {
        handleLocationClick(selectedUser);
      }
    }
  }, [location.state?.selectedUserId, members]);

  const fetchMembers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        pageNumber: currentPage,
        pageSize: pageSize
      };
      // Add location filter if selected
      if (globalLocationFilter) {
        payload.location_id = globalLocationFilter;
      }

      // Add org_id for Super Admin viewing specific organization
      if (isSuperAdmin && superAdminSelectedOrgId) {
        payload.org_id = superAdminSelectedOrgId;
        if (superAdminSelectedSubOrgId) {
          payload.sub_org_id = superAdminSelectedSubOrgId;
        }
      } else if (isAdmin && superAdminSelectedSubOrgId) {
        // Admin drilled into a sub-org via the header dropdown. No org_id
        // override needed/valid for Admin (their own org is implicit via the
        // JWT header) - only sub_org_id.
        payload.sub_org_id = superAdminSelectedSubOrgId;
      }

      const response = await fetchMemberApi(payload); // Fetch from your API

      // Log the entire response to debug
      console.log("API Response:", response);

      // Accessing the data inside the response
      const { businessStatusCode, summaryData, totalPages, totalRecords } = response.data;

      // Check if the response contains the data as expected
      if (businessStatusCode === 2 && summaryData) {
        setMembers(summaryData); // Set the state with the summaryData
        setTotalPages(totalPages || 1);
        setTotalRecords(totalRecords || summaryData.length);
      } else {
        setMembers([]);
        setTotalPages(1);
        setTotalRecords(0);
        setError("No members found. Please add members to get started.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredMembers = members?.filter(
    (member) =>
      (member?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase())) ||
      member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.employee_id?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Sliding 3-page window (current ± 1) to fit narrow panel
  const safePage = Math.min(Math.max(1, currentPage), totalPages || 1);
  const getPageWindow = () => {
    const total = totalPages || 1;
    const WING = 1;
    let start = Math.max(1, safePage - WING);
    let end = Math.min(total, safePage + WING);
    if (end - start < 2) {
      if (start === 1) end = Math.min(total, start + 2);
      else start = Math.max(1, end - 2);
    }
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };
  const pageWindow = getPageWindow();
  const isFirstPage = safePage === 1;
  const isLastPage = safePage >= (totalPages || 1);

  const handleMarkerClick = (segment) => {
    setSelectedSegment(segment);
  };

  const handleLocationClick = async (member) => {
    setSelectedMemberId(member.user_id); // Set the clicked member as selected
    // Update the map center based on the clicked location
    const requestBody = {
      userId: member.user_id, // Send UserID from the clicked member
      date: currentDate.toISOString().split("T")[0], // Use currentDate instead of new Date()
    };
    // Super Admin drilled into an org via the header's Global Filter: scope
    // the selected member's tracking data to that org (see
    // FetchUserTrackingDetails, which validates this server-side).
    if (effectiveOrgId) requestBody.org_id = effectiveOrgId;

    try {
      const response = await fetchLocationForTrackingApi(requestBody); // Fetch from your API

      const { businessStatusCode, segments, message } = response;

      // Check if the response contains the data as expected
      if (businessStatusCode === 2 && segments) {
        // Set the center to the first location in summaryData
        if (segments?.length > 0) {
          setMapCenter({
            lat: segments[0].Latitude,
            lng: segments[0].Longitude,
          });
        }
        // Optionally, set markers for each location
        const locations = segments.map((item) => ({
          name: item.start_time,
          lat: item.latitude,
          lng: item.longitude,
        }));

        // Set the selected locations as an array
        setSelectedLocation(locations);
        setSegments(segments);
        setZoomLevel(18);

        // Fit bounds to show all markers
        const bounds = new window.google.maps.LatLngBounds();
        segments?.forEach((segment) => {
          bounds.extend({ lat: segment.latitude, lng: segment.longitude });
        });
        mapRef.current.fitBounds(bounds);
      } else if (businessStatusCode === 1) {
        // If businessStatusCode is 1, refresh the map (reset state or re-fetch)
        toast.info(message);
        setMapCenter({
          lat: 18.569970140993835,
          lng: 73.77190053167986,
        });
        setSelectedLocation([]);
        setSegments([]);
        setZoomLevel(2);
        fetchMembers();
      } else {
        toast.info("Tracking details not found");
        fetchMembers();
      }
    } catch (err) {
      setError(err.message);
    }

    setZoomLevel(19); // Set zoom level when a location is clicked
  };
  const insidePremisesSegments = segments.filter(
    (segment) => segment.inside_premises
  );

  const formatTime1 = (time) => {
    const [hour, minute] = time.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12; // Convert 0 or 12 to 12 for 12-hour format
    return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const handlePreviousDay = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(prevDate.getDate() - 1);
      return newDate;
    });
  };

  const handleNextDay = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(prevDate.getDate() + 1);
      return newDate;
    });
  };

  const isNextDayDisabled = () => {
    const today = new Date();
    return currentDate.toDateString() === today.toDateString();
  };

  // Add useEffect to fetch data when date changes
  useEffect(() => {
    if (selectedMemberId) {
      handleLocationClick({ user_id: selectedMemberId });
    }
  }, [currentDate]);

  // Show "Select Organization" message if Super Admin hasn't selected an org
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen" style={{ backgroundColor: "#EFF0F4" }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Location Tracking</h1>
            <p className="text-sm text-gray-1000">Super Admin View</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow-sm p-12 mt-8">
          <svg
            className="w-24 h-24 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Select an Organization</h2>
          <p className="text-gray-1000 text-center max-w-md">
            Please select an organization from the dropdown in the header to view location tracking data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0 m-0" >
      <div className="row" style={{backgroundColor:'#EFF0F4'}}>
        <div className="col-12 col-md-12 d-flex justify-content-between align-items-center mt-3 border-bottom text-black-50">
          {/* Search input on left */}
          <div className="relative w-64">
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-1000 focus:outline-none focus:border-gray-300 focus:ring-0 mb-3 shadow-sm"
              placeholder="Search User"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Date picker and buttons on right */}
          <div className="d-flex gap-3 align-items-top">
            <button
              onClick={handlePreviousDay}
              className="p-2 rounded-full"
              style={{color: '#fc5d08', border:'1px solid #fc5d08'}}
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={handleNextDay}
              className="p-2 rounded-full"
              style={{color: '#fc5d08', border:'1px solid #fc5d08'}}
              disabled={isNextDayDisabled()}
              aria-disabled={isNextDayDisabled()}
            >
              <ChevronRight size={14} />
            </button>
            <DatePicker
              selected={currentDate}
              onChange={(date) => setCurrentDate(date)}
              dateFormat="MMM d, yyyy"
              customInput={<CustomDateInput />}
              maxDate={new Date()}
            />
           
          </div>
        </div>

        {/* Members Table */}
        <div className="col-12 col-md-3 text-center border-end text-black-50 bg-white shadow rounded" style={{ height: 'calc(100vh - 151px)', display: 'flex', flexDirection: 'column' }}>
          {isLoading ? (
            <div className="col-12 text-center mt-2">Loading...</div>
          ) : error ? (
            <div className="col-12 text-center mt-2 text-red-1000">{error}</div>
          ) : (
            <div className="d-flex flex-column h-100">
              {/* Table Header */}
              <p className="mt-2 border-bottom text-black-50 fw-bold w-full items-center pb-3" style={{ flexShrink: 0 }}>
                Users List
              </p>

              {/* Table Body - Make this div scrollable */}
              <div className="overflow-auto" style={{ flex: '1 1 auto', overflowY: 'auto', scrollbarWidth:'none', msOverflowStyle:'none'}}>
                {filteredMembers.map((member) => (
                  <div
                    key={member.user_id}
                    onClick={() => handleLocationClick(member)}
                    className={`items-center px-1 py-1 shadow-sm`}
                    style={{
                      padding: "10px",
                      backgroundColor: "#ffffff",
                      color: selectedMemberId === member.user_id ? "#FF8800" : "#333",
                      marginBottom: "1rem",
                      border: selectedMemberId === member.user_id ? "2px solid #FF8800" : "1px solid #eeeeee",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      borderRadius: "20px"
                    }}
                  >
                    <div className=" items-center">
                      <div className="ml-3 flex items-center">
                        <UserAvatar member={member} />
                        <div className="ml-3">
                          <div 
                            className="text-sm font-medium text-gray-900"
                            style={{
                              color: selectedMemberId === member.user_id ? "#FF8800" : "#333"
                            }}
                          >
                            {member.full_name && member.full_name.trim() !== "" && member.full_name !== "null"
                              ? member.full_name
                              : member.email || member.user_id}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls - Fixed at bottom */}
              {totalPages > 1 && (
                <div className="lt-footer">
                  <span className="lt-page-info">
                    {safePage} / {totalPages}
                  </span>
                  <div className="lt-pagination">
                    <button
                      className="lt-page-btn lt-page-nav"
                      onClick={() => setCurrentPage(1)}
                      disabled={isFirstPage}
                      title="First page"
                    >«</button>
                    <button
                      className="lt-page-btn lt-page-nav"
                      onClick={() => setCurrentPage(safePage - 1)}
                      disabled={isFirstPage}
                      title="Previous page"
                    >‹</button>

                    {pageWindow[0] > 1 && (
                      <>
                        <button className="lt-page-btn" onClick={() => setCurrentPage(1)}>1</button>
                        {pageWindow[0] > 2 && <span className="lt-page-ellipsis">…</span>}
                      </>
                    )}

                    {pageWindow.map(n => (
                      <button
                        key={n}
                        className={`lt-page-btn${n === safePage ? ' lt-page-btn--active' : ''}`}
                        onClick={() => setCurrentPage(n)}
                      >
                        {n}
                      </button>
                    ))}

                    {pageWindow.at(-1) < totalPages && (
                      <>
                        {pageWindow.at(-1) < totalPages - 1 && <span className="lt-page-ellipsis">…</span>}
                        <button className="lt-page-btn" onClick={() => setCurrentPage(totalPages)}>
                          {totalPages}
                        </button>
                      </>
                    )}

                    <button
                      className="lt-page-btn lt-page-nav"
                      onClick={() => setCurrentPage(safePage + 1)}
                      disabled={isLastPage}
                      title="Next page"
                    >›</button>
                    <button
                      className="lt-page-btn lt-page-nav"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={isLastPage}
                      title="Last page"
                    >»</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="col-12 col-md-9 shadow-sm rounded">
          {!isMapsLoaded ? (
            <div style={{ height: 'calc(100vh - 151px)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
              Loading map…
            </div>
          ) : (
            <GoogleMap
            className="shadow-sm rounded"
              mapContainerStyle={{ height: 'calc(100vh - 151px)', width: "100%",borderRadius:'3px' }}
              center={mapCenter}
              zoom={zoomLevel}
              onLoad={(map) => (mapRef.current = map)} // Save map instance to ref
            >
              {/* Display markers for segments */}
              {segments.map((segment, index) => (
                <Marker
                  key={index}
                  position={{ lat: segment.latitude, lng: segment.longitude }}
                  onClick={() => handleMarkerClick(segment)}
                  icon={{
                    url: segment.inside_premises
                      ? "http://maps.google.com/mapfiles/ms/icons/green-dot.png"
                      : "http://maps.google.com/mapfiles/ms/icons/red-dot.png", // Change color for inside premises
                    scaledSize: new window.google.maps.Size(30, 30),
                  }}
                />
              ))}

              {/* Group all 'inside_premises' segments in one InfoWindow */}
              {insidePremisesSegments?.length > 0 && (
                <InfoWindow
                  position={{
                    lat: insidePremisesSegments[0].latitude,
                    lng: insidePremisesSegments[0].longitude,
                  }}
                >
                  <div>
                    <span className="fw-bold text-lg">Inside Premises</span>
                    {insidePremisesSegments?.map((segment, index) => {
                      const formatTime = (time) => {
                        const [hour, minute] = time.split(":").map(Number);
                        const period = hour >= 12 ? "PM" : "AM";
                        const formattedHour = hour % 12 || 12; // Convert 0 or 12 to 12 for 12-hour format
                        return `${formattedHour}:${minute
                          .toString()
                          .padStart(2, "0")} ${period}`;
                      };

                      const startTime = formatTime(
                        segment.start_time.split("T")[1].split(".")[0]
                      );
                      const endTime = formatTime(
                        segment.end_time.split("T")[1].split(".")[0]
                      );

                      return (
                        <span className="d-block" key={index}>
                          From {startTime} to {endTime}
                        </span>
                      );
                    })}
                  </div>
                </InfoWindow>
              )}

              {/* Display InfoWindow for individual selected segment */}
              {selectedSegment && (
                <InfoWindow
                  position={{
                    lat: selectedSegment.latitude,
                    lng: selectedSegment.longitude,
                  }}
                  onCloseClick={() => setSelectedSegment(null)}
                >
                  <div>
                    {selectedSegment.inside_premises ? (
                      <div>
                        <span className="fw-bold text-lg">Inside Premises</span>
                        <span className="d-block">
                          From{" "}
                          {formatTime1(
                            selectedSegment.start_time.split("T")[1].slice(0, 5)
                          )}{" "}
                          to{" "}
                          {formatTime1(
                            selectedSegment.end_time.split("T")[1].slice(0, 5)
                          )}
                        </span>
                      </div>
                    ) : (
                      <div>
                        <span className="fw-bold text-lg">
                          Outside Premises
                        </span>
                        <span className="d-block">
                          From{" "}
                          {formatTime1(
                            selectedSegment.start_time.split("T")[1].slice(0, 5)
                          )}{" "}
                          to{" "}
                          {formatTime1(
                            selectedSegment.end_time.split("T")[1].slice(0, 5)
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          )}
        </div>

        <Dialog
          open={isAddMemberOpen}
          onClose={() => setIsAddMemberOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <AddMember
            isOpen={isAddMemberOpen}
            onClose={() => setIsAddMemberOpen(false)}
            onSuccess={handleAddMemberSuccess}
          />
        </Dialog>
      </div>
    </div>
  );
};

export default LocationTracking;
